import { useCallback, useEffect, useState } from 'react'
import { apiGet } from '../generic/Api_Functions'
import { EMPTY_DEVICE_PRODUCTION, fetchDeviceProduction } from './deviceProductionAdapter'
import { fetchDeviceAlarms } from './deviceAlarmsAdapter'

/**
 * useAssetLiveData — data / real-time responsibility of the asset card (RN8-b).
 *
 * Encapsulates the data the card needs: the live device info shown on the card face
 * (current program/tool, read from the snapshot's embedded `device.liveInfo`), the
 * planned locations (`getPlannedLocationsByDevice/<id>`) and IO element set functions
 * (`ioElementSetFunction/<id>`) loaded on demand (when the parts dialog opens), plus the
 * per-device production/alarms detail (on demand). No JSX, no dialogs: data in, data out.
 *
 * Refresh cadence (RN2): `refreshToken` is a counter bumped once by the screen on every
 * periodic tick (currently 2000 ms, see `MonitoringScreenByGroup.jsx`); the on-demand
 * fetches below re-run when it changes. Live info needs no fetch — it rides the snapshot.
 *
 * @param {{id: string|number, hasIoElementSets?: boolean}} device
 * @param {number} refreshToken bumped by the screen on every periodic refresh tick
 * @param {boolean} loadDetails whether to (re)load planned locations and IO element set
 *   functions — mirrors the legacy "only while the parts dialog is open" trigger
 * @param {boolean} loadProduction whether to (re)load the per-device production detail
 *   (`monitoring/deviceProduction/<id>`, story 35): true only where the data is shown —
 *   the fullscreen detail (while open) and the production-mode card. Kept on demand so
 *   the compact grid never pays this per-device call.
 * @param {boolean} loadAlarms whether to (re)load the per-device alarms
 *   (`monitoring/deviceAlarms/<id>`, contract §10.2): true only in the fullscreen detail
 *   (while open), served on demand like the production detail. The card badge does NOT
 *   use this — it reads the summary embedded in the snapshot (`device.alarmSummary`).
 */
export default function useAssetLiveData (device, refreshToken, loadDetails, loadProduction = false, loadAlarms = false) {
  const [deviceInfo, setDeviceInfo] = useState({ programName: '', toolName: '' })
  const [planificationLocations, setPlanificationLocations] = useState([])
  const [ioElementSetFunctions, setIoElementSetFunctions] = useState([])
  const [production, setProduction] = useState(EMPTY_DEVICE_PRODUCTION)
  const [alarms, setAlarms] = useState([])

  // Live info (current program/tool) is embedded in sun's consolidated snapshot as
  // `device.liveInfo` (contract 6.1/6.4-2) — there is no per-card `deviceinfo/<id>` fetch
  // (that endpoint was removed in sun). The device object is replaced on every snapshot
  // tick, so reading `liveInfo` keeps the card in sync with the tick without a request.
  const liveInfo = device?.liveInfo

  useEffect(() => {
    setDeviceInfo({ programName: liveInfo?.programName ?? '', toolName: liveInfo?.toolName ?? '' })
  }, [liveInfo])

  useEffect(() => {
    if (!device?.id || !loadDetails) return
    apiGet('getPlannedLocationsByDevice/' + device.id).then(setPlanificationLocations)
  }, [device?.id, loadDetails])

  useEffect(() => {
    if (!device?.hasIoElementSets || !device?.id || !loadDetails) return
    apiGet('ioElementSetFunction/' + device.id).then(setIoElementSetFunctions)
  }, [device?.hasIoElementSets, device?.id, loadDetails])

  // Production detail (story 35): single per-device call through the adapter, re-run
  // on every refresh tick, but only where it's actually shown (`loadProduction`). When
  // disabled the state stays at the empty default so consumers render nothing.
  useEffect(() => {
    if (!device?.id || !loadProduction) {
      setProduction(EMPTY_DEVICE_PRODUCTION)
      return
    }
    let isCurrent = true
    fetchDeviceProduction(device.id).then((result) => { if (isCurrent) setProduction(result) })
    return () => { isCurrent = false }
  }, [device?.id, refreshToken, loadProduction])

  // Per-device alarms (contract §10.2): same on-demand pattern as production — one call
  // through the adapter, re-run on the refresh tick, only where shown (`loadAlarms`).
  // When disabled the list stays empty so the panel renders its empty state.
  useEffect(() => {
    if (!device?.id || !loadAlarms) {
      setAlarms([])
      return
    }
    let isCurrent = true
    fetchDeviceAlarms(device.id).then((result) => { if (isCurrent) setAlarms(result.alarms) })
    return () => { isCurrent = false }
  }, [device?.id, refreshToken, loadAlarms])

  const loadStoragesByDevice = useCallback(async () => {
    return await apiGet('storages/byDeviceId/' + device.id)
  }, [device.id])

  return { deviceInfo, planificationLocations, ioElementSetFunctions, loadStoragesByDevice, production, alarms }
}
